import { v4 as uuid } from 'uuid';
import apiClient from './apiClient';
import PracticeSettingsService from './PracticeSettingsService';
import { ENCOUNTER_JUDGMENT_POINTS } from './encounterRewards';

export const TRIUMPH_REVIEW_CARD_LIMIT = 100;
export const TRIUMPH_REVIEW_TIME_LIMIT = 30 * 60;

const cardId = card => String(card?.id || card?._id || '');
const lastReviewed = card => {
  const timestamp = new Date(card?.lastPracticedAt || 0).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
};

export function selectTriumphReviewCards(cards = []) {
  const seen = new Set();
  return cards.filter(card => {
    const id = cardId(card);
    if (!id || seen.has(id) || card.isReadingGroup ||
        PracticeSettingsService.getCardType(card) === PracticeSettingsService.CARD_TYPES.READING ||
        !PracticeSettingsService.isGraduated(card)) return false;
    seen.add(id);
    return true;
  }).sort((a, b) => lastReviewed(a) - lastReviewed(b)).slice(0, TRIUMPH_REVIEW_CARD_LIMIT);
}

// Reuses the completed-review persistence contract. No grades, graduation state,
// or NPC win/loss bonuses are changed by this training mode.
export default class TriumphReviewSession {
  constructor(cards, access) {
    this.cards = new Map(cards.map(card => [cardId(card), { ...card }]));
    this.practiceStartId = access.practiceStartId;
    this.sessionId = `triumph-review:${uuid()}`;
    this.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    this.startedAt = null;
    this.results = new Map();
    this.viewedCards = new Set();
    this.repeatCount = 0;
    this.pending = new Map();
    this.saveChain = Promise.resolve();
    this.complete = false;
  }

  start() {
    if (!this.startedAt) this.startedAt = new Date().toISOString();
  }

  recordResult({ card, passed }) {
    const id = cardId(card);
    if (!this.cards.has(id) || this.results.has(id) || this.complete) return { sessionScoreDelta: 0 };
    this.results.set(id, Boolean(passed));
    if (passed) this.updateCard(id, false);
    return { sessionScoreDelta: ENCOUNTER_JUDGMENT_POINTS };
  }

  repeat({ card }) {
    const id = cardId(card);
    if (!this.cards.has(id) || this.complete) return { sessionScoreDelta: 0 };
    this.repeatCount += 1;
    const cardScoreDelta = this.updateCard(id, true);
    return { cardScoreDelta, sessionScoreDelta: 1 };
  }

  answerViewed({ card }) {
    const id = cardId(card);
    if (!this.cards.has(id) || this.viewedCards.has(id) || this.complete) return { sessionScoreDelta: 0 };
    this.viewedCards.add(id);
    return { cardScoreDelta: this.updateCard(id, false, 2), sessionScoreDelta: 2 };
  }

  updateCard(id, repeated, points = repeated ? 1 : 0) {
    const lastPracticedAt = new Date().toISOString();
    const card = this.cards.get(id);
    // Session rewards continue after graduation; the card itself keeps its
    // server-enforced score limit, just like other completed-card reviews.
    const cardScoreDelta = Math.min(points, Math.max(0,
      PracticeSettingsService.getCardScoreLimit(card) - PracticeSettingsService.getCardScore(card)));
    this.cards.set(id, {
      ...card,
      ...(repeated ? {
        practiceCount: PracticeSettingsService.getCardPracticeCount(card) + 1,
      } : {}),
      score: PracticeSettingsService.getCardScore(card) + cardScoreDelta,
      lastPracticedAt,
    });
    const pending = this.pending.get(id) || { id, practiceCountDelta: 0, scoreDelta: 0 };
    this.pending.set(id, {
      ...pending,
      practiceCountDelta: pending.practiceCountDelta + Number(repeated),
      scoreDelta: pending.scoreDelta + cardScoreDelta,
      lastPracticedAt,
    });
    return cardScoreDelta;
  }

  getSummary() {
    const success = Array.from(this.results.values()).filter(Boolean).length;
    return { success, fail: this.results.size - success, cardsStudied: new Set([...this.results.keys(), ...this.viewedCards]).size,
      repeatCount: this.repeatCount, scoreGained: this.repeatCount + this.viewedCards.size * 2 +
        this.results.size * ENCOUNTER_JUDGMENT_POINTS };
  }

  save(duration, isComplete = false) {
    const task = this.saveChain.catch(() => {}).then(async () => {
      if (!this.startedAt || this.complete) return;
      const updates = Array.from(this.pending.values());
      this.pending.clear();
      if (updates.length) {
        try {
          const response = await apiClient.savePracticeProgressBatch({ updates, practiceStartId: this.practiceStartId });
          (response?.updatedCards || []).forEach(saved => {
            const id = cardId(saved);
            const current = this.cards.get(id);
            if (!current) return;
            const newer = this.pending.get(id);
            this.cards.set(id, { ...current, ...saved,
              practiceCount: PracticeSettingsService.getCardPracticeCount(saved) + (newer?.practiceCountDelta || 0),
              score: Math.min(PracticeSettingsService.getCardScoreLimit(saved),
                PracticeSettingsService.getCardScore(saved) + (newer?.scoreDelta || 0)),
              lastPracticedAt: newer?.lastPracticedAt || saved.lastPracticedAt || current.lastPracticedAt,
            });
          });
        } catch (error) {
          // Restore deltas without discarding repetitions made during the request.
          updates.forEach(older => {
            const newer = this.pending.get(older.id);
            this.pending.set(older.id, {
              ...older, ...newer,
              practiceCountDelta: older.practiceCountDelta + (newer?.practiceCountDelta || 0),
              scoreDelta: older.scoreDelta + (newer?.scoreDelta || 0),
            });
          });
          throw error;
        }
      }
      const endTime = new Date(Math.max(Date.now(), Date.parse(this.startedAt) + 1)).toISOString();
      await apiClient.savePracticeSession({
        sessionId: this.sessionId, startTime: this.startedAt, endTime,
        duration: Math.max(1, Math.min(TRIUMPH_REVIEW_TIME_LIMIT, Math.floor(duration))),
        ...this.getSummary(), practiceStartId: this.practiceStartId,
        timezone: this.timezone, mode: 'completedReview', isComplete,
      });
      if (isComplete) this.complete = true;
    });
    this.saveChain = task;
    return task;
  }
}
